import { computed, ref, watch } from "vue";
import { defineStore } from "pinia";
import router from "../../router";
import { api, apiConfig as operatorAPIConfig } from "@lr-notitia/notitia-operator/client";
import { useDomainStore } from "../domain";

export type MessageType = "speech" | "text" | "prefab" | "system";
export type Language =
	| "es"
	| "it"
	| "pt"
	| "en"
	| "pl"
	| "de"
	| "ru"
	| "nl"
	| "fr"
	| "uk"
	| "tr"
	| "zh"
	| "fi"
	| "no"
	| "ro"
	| "sk"
	| "ar"
	| "cs"
	| "el"
	| "da"
	| "hu"
	| "sv"
	| "hr"
	| "id"
	| "mk"
	| "ta"
	| "th"
	| "ja"
	| "ko"
	| "ur"
	| "hi"
	| "az"
	| "bs"
	| "sr"
	| "vi"
	| "lt"
	| "bg"
	| "ms"
	| "sl"
	| "lv"
	| "et"
	| "fa";
export type AlternisMessage = {
	id: string;
	"message-type": MessageType;
	/** Format: int64 */
	created: number;
	/** Format: uint8 */
	sender: number;
	language?: Language | null;
	text: string;
	translations: {
		[key: string]: string;
	};
	"audio-files": {
		[key: string]: string;
	};
	"original-audio-file"?: string | null;
	/** Format: double */
	duration?: number | null;
	"synthesis-played": boolean;
	/** @description runtime property to indicate if the message is being processed */
	"is-processing": boolean;
	progress: {
		speech: "speaking" | "paused" | "ended";
		stt: "processing" | "completed" | "failed" | null;
		trl: { [key: string]: "processing" | "completed" | "failed" | null };
		tts: { [key: string]: "processing" | "completed" | "failed" | null };
	};
};
export type SessionState = "inactive" | "passive" | "active";
export type RtpLanguage =
	| "unknown"
	| "soft-locked-es"
	| "hard-locked-es"
	| "soft-locked-it"
	| "hard-locked-it"
	| "soft-locked-pt"
	| "hard-locked-pt"
	| "soft-locked-en"
	| "hard-locked-en"
	| "soft-locked-pl"
	| "hard-locked-pl"
	| "soft-locked-de"
	| "hard-locked-de"
	| "soft-locked-ru"
	| "hard-locked-ru"
	| "soft-locked-nl"
	| "hard-locked-nl"
	| "soft-locked-fr"
	| "hard-locked-fr"
	| "soft-locked-uk"
	| "hard-locked-uk"
	| "soft-locked-tr"
	| "hard-locked-tr"
	| "soft-locked-zh"
	| "hard-locked-zh"
	| "soft-locked-fi"
	| "hard-locked-fi"
	| "soft-locked-no"
	| "hard-locked-no"
	| "soft-locked-ro"
	| "hard-locked-ro"
	| "soft-locked-sk"
	| "hard-locked-sk"
	| "soft-locked-ar"
	| "hard-locked-ar"
	| "soft-locked-cs"
	| "hard-locked-cs"
	| "soft-locked-el"
	| "hard-locked-el"
	| "soft-locked-da"
	| "hard-locked-da"
	| "soft-locked-hu"
	| "hard-locked-hu"
	| "soft-locked-sv"
	| "hard-locked-sv"
	| "soft-locked-hr"
	| "hard-locked-hr"
	| "soft-locked-id"
	| "hard-locked-id"
	| "soft-locked-mk"
	| "hard-locked-mk"
	| "soft-locked-ta"
	| "hard-locked-ta"
	| "soft-locked-th"
	| "hard-locked-th"
	| "soft-locked-ja"
	| "hard-locked-ja"
	| "soft-locked-ko"
	| "hard-locked-ko"
	| "soft-locked-ur"
	| "hard-locked-ur"
	| "soft-locked-hi"
	| "hard-locked-hi"
	| "soft-locked-az"
	| "hard-locked-az"
	| "soft-locked-bs"
	| "hard-locked-bs"
	| "soft-locked-sr"
	| "hard-locked-sr"
	| "soft-locked-vi"
	| "hard-locked-vi"
	| "soft-locked-lt"
	| "hard-locked-lt"
	| "soft-locked-bg"
	| "hard-locked-bg"
	| "soft-locked-ms"
	| "hard-locked-ms"
	| "soft-locked-sl"
	| "hard-locked-sl"
	| "soft-locked-lv"
	| "hard-locked-lv"
	| "soft-locked-et"
	| "hard-locked-et"
	| "soft-locked-fa"
	| "hard-locked-fa";
export type RtpRegistration = {
	/** Format: uint32 */
	ssrc: number;
	/** Format: uint8 */
	role: number;
	"callback-ip"?: string | null;
	/** Format: uint16 */
	"callback-port"?: number | null;
	language: RtpLanguage;
	"req-domain": string;
};
export type Session = {
	id: string;
	/** Format: int64 */
	created: number;
	/** Format: int64 */
	updated: number;
	department: string;
	label: string;
	/** Format: int64 */
	"message-count": number;
	languages: Language[];
	/** @description runtime property to track the state of the session */
	state: SessionState;
	/** @description runtime property to track the messages in the session it is beeing droped when the session is inactive */
	messages: AlternisMessage[];
	/** @description runtime property to track the rtp registrations in the session it is beeing droped when the session is inactive */
	"rtp-registrations": RtpRegistration[];
};

let domainStore: ReturnType<typeof useDomainStore>;
function init() {
	if (!domainStore) domainStore = useDomainStore();
}

export const useAlternisNewStore = defineStore("alternis_new", () => {
	const workspaceId = computed(() => {
		return router.currentRoute.value.params.workspaceId as string;
	});

	const useNewAlternis = ref(false);
	const sessionId = ref<string | null>(null);
	let eventSource: EventSource | null = null;
	const messages = ref<AlternisMessage[]>([]);
	const isExpanded = ref(false);
	const voiceBotExists = ref(false);

	const sseConnected = ref(false);

	const isVoicebot = computed(() => {
		return (
			voiceBotExists.value ||
			messages.value.filter((m) => m.sender === 0).every((m) => m["message-type"] === "text")
		);
	});

	const sender = ref(0);
	watch(
		isVoicebot,
		(v) => {
			sender.value = v ? 1 : 0;
		},
		{ immediate: true },
	);

	const isInactive = computed(() => {
		return (
			!messages.value[messages.value.length - 1]?.["is-processing"] &&
			messages.value[messages.value.length - 1]?.["message-type"] === "system" &&
			messages.value[messages.value.length - 1].text === "Call ended"
		);
	});

	async function initSSE() {
		init();
		await checkIsNewAlternisWorkspace();
		if (!useNewAlternis.value) return;

		let endpoint = operatorAPIConfig.host;
		endpoint += "/sseAlternisMessages";
		const url = new URL(endpoint, window.location.href);
		url.searchParams.set("domainId", domainStore.domain.id || "");
		url.searchParams.set("workspaceId", workspaceId.value);
		eventSource = new EventSource(url.toString());

		const currentWorkspaceId = workspaceId.value; // workspaceId at the time of connection

		eventSource.onopen = (e) => {
			sseConnected.value = true;
		};
		eventSource.onmessage = (e) => {
			// Ignore messages from other workspace sse connections
			if (workspaceId.value !== currentWorkspaceId) return;

			const data = JSON.parse(e.data) as AlternisMessage[];
			messages.value = data;
		};
		eventSource.onerror = (e) => {
			console.log("SSE error", e);
		};
	}
	function closeSSE() {
		if (!useNewAlternis.value) return;
		if (eventSource) eventSource.close();
		sseConnected.value = false;
	}

	async function checkIsNewAlternisWorkspace() {
		init();
		if (!domainStore?.domain?.id || !workspaceId.value) return;
		const sId = await api()
			.domain(domainStore.domain.id)
			.workspace(workspaceId.value)
			.newAlternisWorkspaces()
			.check()
			.catch(() => null);

		if (sId) {
			useNewAlternis.value = true;
			sessionId.value = sId;
		} else {
			useNewAlternis.value = false;
			sessionId.value = null;
		}
	}

	async function createSessionWithRtp(domainId: string, workspaceId: string, label: string, lang = "unknown") {
		const session = await api()
			.domain(domainId)
			.workspace(workspaceId)
			.alternis()
			.createSessionWithRtp(label, workspaceId, lang)
			.catch((err) => {
				console.error("Error creating session with RTP:", err);
				return null;
			});

		if (session) {
			useNewAlternis.value = true;
			sessionId.value = session.id;
			initSSE();
		}
	}

	async function endSession(domainId: string, workspaceId: string) {
		if (!sessionId.value) return;
		// TODO REMOVE TIMEOUT WHEN ALTERNIS IS FIXED TO HANDLE PROPERLY THE END SESSION REQUEST - jnhck 20.11.2025
		setTimeout(async () => {
			await api()
				.domain(domainId)
				.workspace(workspaceId)
				.alternis()
				.endSession()
				.catch((err) => {
					console.error("Failed to end session:", err);
				});

			closeSSE();
		}, 5000);
	}

	async function sendTextMessage(
		domainId: string,
		workspaceId: string,
		text: string,
		options: { sender: number; emitImmediately: boolean; language: Language },
	) {
		await api()
			.domain(domainId)
			.workspace(workspaceId)
			.alternis()
			.sendTextMessage(text, options)
			.catch((err) => {
				console.error("Error sending message:", err);
			});
	}

	async function playTranslation(domainId: string, workspaceId: string, messageId: string) {
		await api()
			.domain(domainId)
			.workspace(workspaceId)
			.alternis()
			.emitTranslationSynthesis(messageId)
			.catch((err) => {
				console.error("Error playing translation:", err);
			});
	}

	async function checkVoiceBotExists(domainId: string, dataspaceId: string, workspaceId: string) {
		const exists = await api()
			.domain(domainId)
			.workspace(workspaceId)
			.voiceBot(dataspaceId)
			.exists()
			.catch((err) => {
				console.error("Error checking voice bot existence:", err);
				return false;
			});
		voiceBotExists.value = exists;
	}
	async function createVoiceBot(domainId: string, dataspaceId: string, workspaceId: string) {
		await api()
			.domain(domainId)
			.workspace(workspaceId)
			.voiceBot(dataspaceId)
			.create()
			.catch((err) => {
				console.error("Failed to create voice bot:", err);
			});
		voiceBotExists.value = true;
	}

	function clear() {
		useNewAlternis.value = false;
		sessionId.value = null;
		messages.value = [];
		sender.value = 0;
	}

	watch(
		() => workspaceId.value,
		() => checkIsNewAlternisWorkspace(),
		{ immediate: true },
	);

	return {
		useNewAlternis,
		sessionId,
		checkIsNewAlternisWorkspace,
		messages,
		isExpanded,
		initSSE,
		closeSSE,
		createSessionWithRtp,
		endSession,
		isVoicebot,
		isInactive,
		sendTextMessage,
		playTranslation,
		createVoiceBot,
		voiceBotExists,
		checkVoiceBotExists,
		sseConnected,
		sender,
		clear,
	};
});
